import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/lib/auth';
import { supabase } from '@/lib/supabase';
import { normalizePatientCommunicationPrefs } from '@/lib/patientCommunication';
import {
  EMPTY_PATIENT_UI_PREFS,
  parsePatientUiPrefsCache,
  serializePatientUiPrefsCache,
  type PatientUiPrefs,
} from '@/lib/patientUiPrefs';

/**
 * The signed-in user's patient page preferences (`user_ui_preferences`):
 * `order` = patient_tab_order (null = default order), `hidden` = patient_tabs_hidden,
 * `patientInfoCollapsed` = patient_info_collapsed (Patientoplysninger; one choice for all tabs; closed default),
 * `tabsLocked` = patient_tabs_locked (true = cannot drag tabs; default locked),
 * `journalFullscreenShowHistory` = journal_fullscreen_show_history (fullscreen note editor).
 *
 * - First paint uses the copy cached in localStorage; the server row is the source of truth
 *   and replaces it once loaded.
 * - Changes update the UI at once (optimistic) and save debounced; a pending save is flushed
 *   when the page unmounts.
 */
const SAVE_DEBOUNCE_MS = 600;

export type PatientTabPrefs = PatientUiPrefs;

const EMPTY: PatientTabPrefs = EMPTY_PATIENT_UI_PREFS;

export const patientTabPrefsQueryKey = (userId: string | null) =>
  ['user-ui-preferences', 'patient-tabs', userId] as const;

function cacheKey(userId: string) {
  return `clinio_patient_tab_order:${userId}`;
}

function readCache(userId: string | null): PatientTabPrefs {
  if (!userId) return EMPTY;
  try {
    return parsePatientUiPrefsCache(localStorage.getItem(cacheKey(userId)));
  } catch {
    return EMPTY;
  }
}

function writeCache(userId: string, prefs: PatientTabPrefs) {
  try {
    const raw = serializePatientUiPrefsCache(prefs);
    if (raw === null) localStorage.removeItem(cacheKey(userId));
    else localStorage.setItem(cacheKey(userId), raw);
  } catch {
    // localStorage not available
  }
}

export function usePatientTabPrefs() {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const qc = useQueryClient();
  const [prefs, setLocalPrefs] = useState<PatientTabPrefs>(() => readCache(userId));
  const prefsRef = useRef(prefs);
  prefsRef.current = prefs;
  const dirty = useRef(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pending = useRef<{ userId: string; prefs: PatientTabPrefs } | null>(null);

  const query = useQuery({
    queryKey: patientTabPrefsQueryKey(userId),
    enabled: Boolean(userId),
    queryFn: async (): Promise<PatientTabPrefs> => {
      if (!userId) return EMPTY;
      const { data, error } = await supabase
        .from('user_ui_preferences')
        .select(
          'patient_tab_order, patient_tabs_hidden, patient_info_collapsed, patient_tabs_locked, journal_fullscreen_show_history',
        )
        .eq('user_id', userId)
        .maybeSingle();
      if (error) throw error;
      return normalizePatientCommunicationPrefs({
        order: data?.patient_tab_order ?? null,
        hidden: data?.patient_tabs_hidden ?? [],
        patientInfoCollapsed: data?.patient_info_collapsed ?? true,
        tabsLocked: data?.patient_tabs_locked ?? true,
        journalFullscreenShowHistory: data?.journal_fullscreen_show_history ?? true,
      });
    },
    staleTime: 60_000,
  });

  // A different user signed in: start from their cached copy.
  useEffect(() => {
    setLocalPrefs(readCache(userId));
  }, [userId]);

  // Server is the source of truth (unless the user has a change pending).
  useEffect(() => {
    if (!userId || query.data === undefined || dirty.current) return;
    setLocalPrefs(query.data);
    writeCache(userId, query.data);
  }, [userId, query.data]);

  const save = useCallback(
    async (target: { userId: string; prefs: PatientTabPrefs }) => {
      const { error } = await supabase.from('user_ui_preferences').upsert(
        {
          user_id: target.userId,
          patient_tab_order: target.prefs.order,
          patient_tabs_hidden: target.prefs.hidden,
          patient_info_collapsed: target.prefs.patientInfoCollapsed,
          patient_tabs_locked: target.prefs.tabsLocked,
          journal_fullscreen_show_history: target.prefs.journalFullscreenShowHistory,
        },
        { onConflict: 'user_id' },
      );
      if (pending.current === null) dirty.current = false;
      if (error) {
        console.error('saving patient tab preferences failed', error);
        void qc.invalidateQueries({ queryKey: patientTabPrefsQueryKey(target.userId) });
        return;
      }
      qc.setQueryData(patientTabPrefsQueryKey(target.userId), target.prefs);
    },
    [qc],
  );

  const flush = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
    const target = pending.current;
    pending.current = null;
    if (target) void save(target);
  }, [save]);

  // Leaving the page within the debounce window still saves the last change.
  useEffect(() => () => flush(), [flush]);

  const update = useCallback(
    (patch: Partial<PatientTabPrefs>) => {
      const next: PatientTabPrefs = { ...prefsRef.current, ...patch };
      prefsRef.current = next;
      setLocalPrefs(next);
      if (!userId) return;
      writeCache(userId, next);
      dirty.current = true;
      pending.current = { userId, prefs: next };
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(flush, SAVE_DEBOUNCE_MS);
    },
    [userId, flush],
  );

  const setOrder = useCallback((order: string[] | null) => update({ order }), [update]);
  const setHidden = useCallback((hidden: string[]) => update({ hidden }), [update]);
  const setPatientInfoCollapsed = useCallback(
    (patientInfoCollapsed: boolean) => update({ patientInfoCollapsed }),
    [update],
  );
  const setTabsLocked = useCallback((tabsLocked: boolean) => update({ tabsLocked }), [update]);
  const setJournalFullscreenShowHistory = useCallback(
    (journalFullscreenShowHistory: boolean) => update({ journalFullscreenShowHistory }),
    [update],
  );
  const reset = useCallback(() => update({ order: null, hidden: [] }), [update]);

  return {
    order: prefs.order,
    hidden: prefs.hidden,
    patientInfoCollapsed: prefs.patientInfoCollapsed,
    tabsLocked: prefs.tabsLocked,
    journalFullscreenShowHistory: prefs.journalFullscreenShowHistory,
    setOrder,
    setHidden,
    setPatientInfoCollapsed,
    setTabsLocked,
    setJournalFullscreenShowHistory,
    reset,
  };
}
